/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: react.js library.
import JoiBrowser from 'joi-browser' // Npm: Joi for frontend validation.
import _ from 'underscore' // Npm: Underscore.js library.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { toast } from 'react-hot-toast' // Npm: React hot toast.
import { useLazyQuery, useMutation } from '@apollo/client' // Npm: Apollo client.


/*
 * PACKAGES
 */
import SubmitButton from 'components/SubmitButton'
import { MemoizedInput } from 'components/MemoizedInput'


/*
 * GRAPHS
 */
import ContactBookDirectoryReadUniqueQuery from './__query__/index.contactBookDirectory.read.query'
import ContactBookDirectoryUpsertMutation from './__mutation__/index.contactBookDirectory.upsert.mutation'


/*
 * OBJECTS
 */
const Index = ({ customer, isOpen, isCreateOnly, onClose, passOn }) => {
  // Hook assignment.
  const [error, setError] = React.useState('')
  const [forceReRender, setForceReRender] = React.useState('')
  const [QueryContactBookDirectoryReadUnique, QueryContactBookDirectoryReadUniqueResponse] = useLazyQuery(ContactBookDirectoryReadUniqueQuery, { 'variables': { 'contactBookDirectoryId': passOn?.contactBookDirectoryId } })
  const [MutationContactBookDirectoryUpsert, MutationContactBookDirectoryUpsertResponse] = useMutation(ContactBookDirectoryUpsertMutation)
  const _formDataRef = React.useRef({})

  // Object assignment.
  const _SubmitForm = async e => {
    // Prevent default behavior.
    e.preventDefault()

    // Reset error.
    setError('')

    // Const assignment.
    const _JoiSchema = JoiBrowser.object({
      'displayName': JoiBrowser.string().required()
    }).options({ 'allowUnknown': true })

    // Remove all keys from _formDataRef.current which are undefined.
    _formDataRef.current = _.pick(_formDataRef.current, _.identity)

    // Validate form data.
    const _JoiSchemaValidate = _JoiSchema.validate(_formDataRef.current)

    // If error exists then report failure.
    if (_JoiSchemaValidate.error) return setError(_JoiSchemaValidate.error?.message)

    // Execute customer registration mutation.
    const _MutationContactBookDirectoryUpsert = await MutationContactBookDirectoryUpsert({
      'variables': {
        'customerId': customer?.id,
        'displayName': _formDataRef?.current?.displayName,
        'contactBookDirectoryId': isCreateOnly ? void 0 : passOn?.contactBookDirectoryId
      }
    })

    // If mutation caught an exception then report failure.
    if (_MutationContactBookDirectoryUpsert instanceof Error) return _MutationContactBookDirectoryUpsert

    // On Successful response from the mutation.
    if ('UPSERT_SUCCESSFUL' === _MutationContactBookDirectoryUpsert?.data?.ContactBookDirectoryUpsert?.status) onClose?.()

    // Style Guide.
    return toast(_MutationContactBookDirectoryUpsert?.data?.ContactBookDirectoryUpsert?.message)
  }

  // Event handler.
  React.useEffect(() => {
    // _Async handler.
    const _Async = async () => {
      // Const assignment.
      const _QueryContactBookDirectoryReadUniqueQuery = await QueryContactBookDirectoryReadUnique({ 'variables': { 'contactBookDirectoryId': isCreateOnly ? 'UN_KNOWN' : passOn?.contactBookDirectoryId } })

      // If query caught an exception then report failure.
      if (_QueryContactBookDirectoryReadUniqueQuery instanceof Error) return _QueryContactBookDirectoryReadUniqueQuery

      /*
       * If customer details fetch complete then
       * update its value.
       */
      if (_QueryContactBookDirectoryReadUniqueQuery?.data?.ContactBookDirectoryReadUnique) {
        // Update form data.
        _formDataRef.current = {
          'contactBookDirectoryId': passOn?.contactBookDirectoryId,
          'displayName': _QueryContactBookDirectoryReadUniqueQuery?.data?.ContactBookDirectoryReadUnique?.displayName
        }

        // Update state.
        return setForceReRender(String.random(8))
      }

      // Report failure.
      return void 0
    }; _Async().catch(i => i)
  }, [passOn, isOpen])

  // Const assignment.
  const _isLoading = MutationContactBookDirectoryUpsertResponse.loading
  const _isInputDisabled = isCreateOnly ? false : _isLoading || QueryContactBookDirectoryReadUniqueResponse?.loading

  // Return component.
  return (
    <form style={{ 'width': '100%' }} onSubmit={_SubmitForm} key={forceReRender}>
      <MemoizedInput
        disabled={_isInputDisabled}
        isRequired={true}
        name='displayName'
        label='Directory Name'
        placeholder='e.g. "Test Numbers"'
        onChange={({ target }) => {
          // Over spreading.
          const { name, value } = target

          // Update form data.
          _formDataRef.current = {
            ..._formDataRef?.current,
            [name]: value
          }
        }}
        error={error}
        isInvalid={error?.includes('displayName')}
        data={_formDataRef?.current?.displayName}
      />
      <SubmitButton
        isLoading={_isLoading}
        defaultText={isCreateOnly ? 'Create Folder' : 'Update Folder'}
        disabled={_isInputDisabled}
        onSubmit={_SubmitForm}
      />
    </form>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'isCreateOnly': PropTypes.bool,
  'isOpen': PropTypes.bool,
  'onClose': PropTypes.func,
  'passOn': PropTypes.object
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'customer': __state.Customer, 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
